"use strict";
/**
 * Flag Definition Types (Code Layer)
 *
 * This module defines the TYPE/SCHEMA layer for feature flags.
 * Flag definitions declare that a flag EXISTS and what its VALUE TYPE is.
 *
 * IMPORTANT: This is the CODE layer - it defines ONLY:
 * - Flag identity (key)
 * - Value type and constraints (boolean, string with allowed values, number with min/max, json with schema)
 * - Metadata (description, owner, tags)
 * - Sensitivity (whether value can be exposed to client)
 *
 * All EVALUATION LOGIC (defaults, rules, rollouts, dependencies) lives in the DATA layer
 * (FlagProgram) which is stored in Firestore and manipulated via admin UI.
 *
 * TYPE SAFETY: This module uses discriminated unions to ensure type information flows
 * through the entire pipeline. Each flag type (boolean, string, number, json) has its
 * own strongly-typed definition that carries the value type at the TypeScript level.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.FlagRegistrySchema = exports.FlagDefinitionSchema = exports.FlagEnvironment = exports.TypeConstraints = exports.JsonConstraints = exports.StringConstraints = exports.NumberConstraints = exports.FlagValueType = void 0;
exports.defineBooleanFlag = defineBooleanFlag;
exports.defineStringFlag = defineStringFlag;
exports.defineNumberFlag = defineNumberFlag;
exports.defineJsonFlag = defineJsonFlag;
const zod_1 = require("zod");
/**
 * Supported flag value types as string literals
 */
exports.FlagValueType = zod_1.z.enum(["boolean", "string", "number", "json"]);
/**
 * Type constraints for number flags
 */
exports.NumberConstraints = zod_1.z.object({
    min: zod_1.z.number().optional(),
    max: zod_1.z.number().optional(),
});
/**
 * Type constraints for string flags (enum-like)
 */
exports.StringConstraints = zod_1.z.object({
    allowedValues: zod_1.z.array(zod_1.z.string()).optional(),
});
/**
 * Type constraints for JSON flags
 * For MVP, we use a simple schema definition
 */
exports.JsonConstraints = zod_1.z.object({
    schema: zod_1.z.record(zod_1.z.unknown()).optional(),
});
/**
 * Union of all type constraints
 */
exports.TypeConstraints = zod_1.z.union([exports.NumberConstraints, exports.StringConstraints, exports.JsonConstraints]);
/**
 * Environment names for flag configuration
 */
exports.FlagEnvironment = zod_1.z.enum(["development", "staging", "production"]);
/**
 * Flag definition schema - the TYPE/SCHEMA of a flag as defined in code
 *
 * This defines ONLY the flag's existence and value type, NOT how it's evaluated.
 * All evaluation logic (defaults, rules, dependencies) is in FlagProgram (data layer).
 */
exports.FlagDefinitionSchema = zod_1.z.object({
    /**
     * Unique identifier for the flag (kebab-case recommended)
     */
    key: zod_1.z.string().min(1),
    /**
     * Human-readable description of what the flag controls
     */
    description: zod_1.z.string(),
    /**
     * Team or person responsible for this flag
     */
    owner: zod_1.z.string().optional(),
    /**
     * When this flag was created
     */
    createdAt: zod_1.z.number().optional(),
    /**
     * Tags for categorization and filtering
     */
    tags: zod_1.z.array(zod_1.z.string()).default([]),
    /**
     * The value type of this flag
     */
    type: exports.FlagValueType,
    /**
     * Type-specific constraints (min/max for numbers, allowed values for strings, etc.)
     * These are enforced at config-write time and evaluation time.
     */
    typeConstraints: exports.TypeConstraints.optional(),
    /**
     * If true, this flag's value should not be exposed to the client
     * (still evaluated but only available server-side)
     */
    sensitive: zod_1.z.boolean().default(false),
});
/**
 * Registry of all flag definitions (code layer)
 */
exports.FlagRegistrySchema = zod_1.z.record(zod_1.z.string(), exports.FlagDefinitionSchema);
/**
 * Helper to create a boolean flag definition (code layer only - no evaluation logic)
 * Returns a strongly typed BooleanFlagDefinition.
 */
function defineBooleanFlag(key, config) {
    return {
        key,
        type: "boolean",
        description: config.description,
        owner: config.owner,
        tags: config.tags ?? [],
        sensitive: config.sensitive ?? false,
    };
}
/**
 * Helper to create a string flag definition (code layer only - no evaluation logic)
 * Returns a strongly typed StringFlagDefinition.
 */
function defineStringFlag(key, config) {
    const result = {
        key,
        type: "string",
        description: config.description,
        owner: config.owner,
        tags: config.tags ?? [],
        sensitive: config.sensitive ?? false,
    };
    if (config.allowedValues) {
        result.typeConstraints = { allowedValues: config.allowedValues };
    }
    return result;
}
/**
 * Helper to create a number flag definition (code layer only - no evaluation logic)
 * Returns a strongly typed NumberFlagDefinition.
 */
function defineNumberFlag(key, config) {
    const result = {
        key,
        type: "number",
        description: config.description,
        owner: config.owner,
        tags: config.tags ?? [],
        sensitive: config.sensitive ?? false,
    };
    if (config.min !== undefined || config.max !== undefined) {
        result.typeConstraints = { min: config.min, max: config.max };
    }
    return result;
}
/**
 * Helper to create a JSON flag definition (code layer only - no evaluation logic)
 * Returns a strongly typed JsonFlagDefinition.
 *
 * @template T - The TypeScript type of the JSON value (inferred from valueSchema if provided)
 */
function defineJsonFlag(key, config) {
    const result = {
        key,
        type: "json",
        description: config.description,
        owner: config.owner,
        tags: config.tags ?? [],
        sensitive: config.sensitive ?? false,
    };
    if (config.schema) {
        result.typeConstraints = { schema: config.schema };
    }
    if (config.valueSchema) {
        result.valueSchema = config.valueSchema;
    }
    return result;
}
//# sourceMappingURL=definitions.js.map